import * as React from "react";

import { TextComponentNameLevel1 } from "./textConstants";
import {
  AppDefinedVariables,
  Keys,
  TextComponentConfig,
} from "./textInterfaces";

import { useFlags } from "@unleash/proxy-client-react";
import { HintProps } from "Components/Form/RadioButton/radioButtonInterfaces";
import { useEffect } from "react";
import { TextContext } from "./TextContext";
import { TextManager } from "./TextManager";

export const TextProvider = (
  props: React.PropsWithChildren<unknown>,
): React.ReactElement => {
  const flags = useFlags();
  useEffect(() => TextManager.setFlags(flags), [flags]);

  const { children } = props;
  const [globalVariables, setGlobalVariables] =
    React.useState<AppDefinedVariables>({});

  const updateVariables = React.useCallback(
    (payload: AppDefinedVariables) =>
      setGlobalVariables((prev) => ({ ...prev, ...payload })),
    [],
  );

  const getText = React.useCallback(
    (id: Keys, config: TextComponentConfig = {}) =>
      TextManager.getText(id, {
        ...config,
        variables: {
          ...globalVariables,
          ...config.variables,
        },
      }),
    [globalVariables],
  );

  const getTexts = React.useCallback(
    (
      ids: Array<Keys>,
      config: TextComponentConfig = {},
      separator: string | Array<string> = " ",
    ) =>
      TextManager.getTexts(
        ids,
        {
          ...config,
          variables: { ...globalVariables, ...config.variables },
        },
        separator,
      ),
    [globalVariables],
  );

  const createLinkComponent = React.useCallback(
    (id: Keys, config: TextComponentConfig = {}, isFormHint = false) =>
      TextManager.createLinkComponent(
        id,
        {
          variant: config.variant,
          variables: config.variables,
        },
        isFormHint,
      ),
    [],
  );

  const getSelectOptions = React.useCallback(
    (
      textComponentNameLevel1: TextComponentNameLevel1,
      formFieldId: string,
      placeholder: string,
      config?: TextComponentConfig,
    ) =>
      TextManager.getSelectOptions(
        textComponentNameLevel1,
        formFieldId,
        placeholder,
        {
          variables: {
            ...globalVariables,
            ...config?.variables,
          },
          ...config,
        },
      ),
    [globalVariables],
  );

  const getRadioOptions = React.useCallback(
    (
      textComponentNameLevel1: TextComponentNameLevel1,
      formFieldId: string,
      config?: TextComponentConfig,
      hintProps?: HintProps,
    ) =>
      TextManager.getRadioOptions(
        textComponentNameLevel1,
        formFieldId,
        {
          variables: {
            ...globalVariables,
            ...config?.variables,
          },
          ...config,
        },
        hintProps,
      ),
    [globalVariables],
  );

  return (
    <TextContext.Provider
      value={{
        getText,
        getTexts,
        updateVariables,
        variables: globalVariables,
        createLinkComponent,
        getSelectOptions,
        getRadioOptions,
      }}
    >
      {children}
    </TextContext.Provider>
  );
};
